import { selectors } from 'modules/common';
import { domain, stateTree } from 'modules/data';
import { integrationActions } from 'modules/integration';
import { useSelector } from 'modules/react-redux';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useMount } from 'react-use';
import { useCompanyBasedOnUrl } from 'shared/hooks/useCompanyBasedOnUrl';

import {
    applyReportFilters,
    cancelActivePopup,
    updateDynamicReportColumns,
    updateFiltersPopupReportConfig,
} from '../../actions';
import ColumnKind from '../../config/standard/columnKind';
import { ReferenceListFilter } from '../../data/filters/referenceListFilter';
import * as reportFiltersPopup from '../../reducers/page/reportFiltersPopupReducer';
import { reportFiltersPopupId } from '../../reducers/page/reportFiltersPopupReducer';
import { getActivePopup } from '../../selectors/pageSelectors';
import { ReportConfig } from '../../types/ReportConfig';
import { tabsItems } from './ReportEditPopup.constants';
import { ActiveTab, ReportEditPopupProps } from './ReportEditPopup.types';

export const useReportEditPopupWindow = () => {
    const dispatch = useDispatch();

    const popup = useSelector(getActivePopup<reportFiltersPopup.ReportFiltersPopupData>);
    const isOpen = popup?.id === reportFiltersPopupId;

    const handleClose = useCallback(
        (open: boolean) => {
            !open && dispatch(cancelActivePopup());
        },
        [dispatch]
    );

    return {
        isOpen,
        handleClose,
    };
};

export const useReportEditPopupTabs = (reportConfig: ReportEditPopupProps['reportConfig']) => {
    const [tabs, setTabs] = useState(tabsItems);
    const [activeTab, setActiveTab] = useState(ActiveTab.ReportType);

    const isRequestTypeSelected = useCheckIsRequestTypeSelected(reportConfig);

    const templateColumn = useMemo(
        () => reportConfig.columns.find((column) => column.kind === ColumnKind.TemplateId),
        [reportConfig.columns]
    );

    const requestTypeSelected =
        (reportConfig.reportType === domain.ReportType.LineItem && reportConfig.reportCode) ||
        (reportConfig.reportType === domain.ReportType.Request &&
            (templateColumn?.filter as ReferenceListFilter).values.length);

    const handleChangeTab = useCallback(
        (tabId: ActiveTab) => {
            if (requestTypeSelected || tabId === ActiveTab.ReportType) {
                setActiveTab(tabId);
            }
        },
        [requestTypeSelected]
    );

    useMount(() => {
        if (reportConfig.reportType === domain.ReportType.LineItem && reportConfig.lastRunDate) {
            setTabs((tabs) => tabs.filter((item) => item.value !== ActiveTab.ReportType));
            setActiveTab(ActiveTab.VisibleColumns);
        }
    });

    useEffect(() => {
        setTabs((tabs) =>
            tabs.map((item) => ({
                ...item,
                disabled: item.value !== ActiveTab.ReportType ? !isRequestTypeSelected : false,
            }))
        );
    }, [isRequestTypeSelected]);

    return {
        tabs,
        activeTab,
        handleChangeTab,
    };
};

export const usePopupReportEdit = (reportConfig: ReportEditPopupProps['reportConfig']) => {
    const dispatch = useDispatch();

    const company = useCompanyBasedOnUrl();

    const popup = useSelector(getActivePopup<reportFiltersPopup.ReportFiltersPopupData>);
    const isModified = !!popup?.modified;

    const integration = useSelector((state) => selectors.integration.getIntegrationByCompanyId(state, company.id));
    const hasActiveIntegration = useSelector((state) => selectors.integration.hasActiveIntegration(state, company.id));

    const isLoading = useSelector((state) => {
        const isLoadingIntegrationFields =
            !!integration &&
            hasActiveIntegration &&
            selectors.field.fieldSetIsLoading(state, {
                type: stateTree.FieldSetType.AllIntegrationFields,
                companyId: company.id,
            });
        const isLoadingTemplateFields = selectors.field.fieldSetIsLoading(state, {
            type: stateTree.FieldSetType.TemplateFields,
            companyId: company.id,
            templateIntegrationCode: null,
        });

        return isLoadingIntegrationFields || isLoadingTemplateFields;
    });

    const isQboLineItem =
        popup?.reportConfig?.reportType === domain.ReportType.LineItem &&
        integration?.integrationType === domain.IntegrationType.QBooks;

    useEffect(() => {
        dispatch(integrationActions.loadAllCompanyFields({ companyId: company.id }));
    }, [dispatch, company.id]);

    useEffect(() => {
        if (!isLoading && !isQboLineItem) {
            dispatch(updateDynamicReportColumns({ reportConfig }));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch, isQboLineItem, isLoading]);

    const handleSave = useCallback(() => {
        if (isModified) {
            if (popup?.reportConfig) {
                dispatch(applyReportFilters(popup?.reportConfig));
            }
        } else {
            dispatch(cancelActivePopup());
        }
    }, [dispatch, isModified, popup?.reportConfig]);

    const handleChangeReportConfig = useCallback(
        (newReportConfig: ReportConfig) => {
            dispatch(updateFiltersPopupReportConfig(newReportConfig));
        },
        [dispatch]
    );

    return {
        isLoading,
        handleSave,
        handleChangeReportConfig,
    };
};

export const useCheckIsRequestTypeSelected = (reportConfig: ReportEditPopupProps['reportConfig']) => {
    const templateColumn = reportConfig.columns.find((column) => column.kind === ColumnKind.TemplateId);

    switch (reportConfig.reportType) {
        case domain.ReportType.Request:
            return !!(templateColumn?.filter as ReferenceListFilter).values.length;

        case domain.ReportType.LineItem:
            return reportConfig.reportCode !== null;
    }
};
